import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { markdownToHtml } from '../ai/utils/markdown';
import { useAuth } from '../../contexts/AuthContext';

interface PageData {
  id: number;
  slug: string;
  title: string;
  content: string;
  meta_description: string | null;
  published_at: string | null;
  updated_at: string;
}

export default function PageView() {
  const { slug } = useParams<{ slug: string }>();
  const { user, hasPermission } = useAuth();
  const isAdmin = !!user && hasPermission?.('system.config');
  const [page, setPage] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [toc, setToc] = useState<Array<{ id: string; text: string; level: number }>>([]);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`/api/pages/${slug}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.page) {
          setPage(data.page);
          document.title = `${data.page.title} | EIS Tech`;

          // Generate TOC from markdown headings and store page as-is
          // IDs are injected into the rendered HTML via useEffect below
          const tocItems: Array<{ id: string; text: string; level: number }> = [];
          const headingRegex = /^(#{1,3})\s+(.+)$/gm;
          let match;
          while ((match = headingRegex.exec(data.page.content)) !== null) {
            const level = match[1]!.length;
            const text = match[2]!.trim();
            const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            if (level >= 2) tocItems.push({ id, text, level });
          }
          setToc(tocItems);
          setPage(data.page);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Inject IDs into rendered headings for TOC anchor links
  useEffect(() => {
    if (!page || toc.length === 0) return;
    const container = document.querySelector('.pv-content');
    if (!container) return;
    const headings = container.querySelectorAll('h1, h2, h3');
    headings.forEach(heading => {
      const text = (heading.textContent || '').trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      heading.id = id;
    });
  }, [page, toc]);

  if (loading) {
    return (
      <div className="pv-loading">
        <div className="pv-spinner" />
        <style>{pageStyles}</style>
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="pv-notfound">
        <div className="pv-notfound-code">404</div>
        <p className="pv-notfound-text">This page hasn't been published yet.</p>
        <Link to="/" className="pv-link-home">Back to home</Link>
        <style>{pageStyles}</style>
      </div>
    );
  }

  return (
    <div className="pv-root">
      <style>{pageStyles}</style>

      {/* Sticky nav */}
      <nav className="pv-nav">
        <div className="pv-nav-inner">
          <Link to="/" className="pv-logo">
            <svg width="28" height="28" viewBox="0 0 512 512" fill="none">
              <defs>
                <linearGradient id="pvbg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0EA5E9"/><stop offset="100%" stopColor="#0369A1"/></linearGradient>
                <linearGradient id="pvlf" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#84CC16"/><stop offset="100%" stopColor="#3F6212"/></linearGradient>
              </defs>
              <path d="M256 56 C390 56 456 150 456 256" fill="none" stroke="url(#pvbg)" strokeWidth="28" strokeLinecap="round"/>
              <path d="M56 256 C56 150 122 56 256 56" fill="none" stroke="url(#pvbg)" strokeWidth="28" strokeLinecap="round"/>
              <circle cx="256" cy="180" r="38" fill="url(#pvbg)"/>
              <path d="M170 260 Q256 340 256 320 Q256 340 342 260" fill="url(#pvbg)"/>
              <path d="M256 340 Q140 340 100 420 Q180 380 256 456 Z" fill="url(#pvlf)"/>
              <path d="M256 340 Q372 340 412 420 Q332 380 256 456 Z" fill="url(#pvlf)"/>
            </svg>
            <span className="pv-logo-text">EIS</span>
          </Link>
          <div className="pv-nav-right">
            <Link to="/" className="pv-nav-link">Home</Link>
            <Link to="/contact" className="pv-nav-link">Contact</Link>
            <Link to="/login" className="pv-nav-cta">Sign in</Link>
          </div>
        </div>
      </nav>

      {/* Hero header */}
      <header className="pv-hero">
        <div className="pv-hero-glow" />
        <div className="pv-hero-inner">
          <div className="pv-breadcrumb">
            <Link to="/">Home</Link>
            <span className="pv-breadcrumb-sep">/</span>
            <span>{page.title}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <h1 className="pv-title" style={{ margin: 0 }}>{page.title}</h1>
            {isAdmin && (
              <Link
                to={`/admin/pages?edit=${page.id}`}
                className="pv-edit-btn"
                title="Edit this page"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit Page
              </Link>
            )}
          </div>
          {page.updated_at && (
            <p className="pv-date">
              Last updated: {new Date(page.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="pv-main">
        {toc.length > 0 && (
          <aside className="pv-toc">
            <h3 className="pv-toc-title">Table of Contents</h3>
            <nav className="pv-toc-nav">
              {toc.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={`pv-toc-link pv-toc-level-${item.level}`}
                >
                  {item.text}
                </a>
              ))}
            </nav>
          </aside>
        )}
        <article
          className="pv-content"
          dangerouslySetInnerHTML={{ __html: markdownToHtml(page.content) }}
        />
      </main>

      {/* Footer */}
      <footer className="pv-footer">
        <div className="pv-footer-inner">
          <p className="pv-footer-company">&copy; {new Date().getFullYear()} EIS-Tech &middot; Powered by Yiontech Ltd &middot; Registered in England &amp; Wales</p>
          <div className="pv-footer-links">
            <Link to="/page/privacy-policy">Privacy</Link>
            <Link to="/page/terms-of-service">Terms</Link>
            <Link to="/page/cookie-policy">Cookies</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const pageStyles = `
  /* ─── Base ──────────────────────────────────────────── */
  .pv-root {
    min-height: 100vh;
    background-color: #ffffff;
    color: #1e293b;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    -webkit-font-smoothing: antialiased;
    text-align: left;
    margin: 0;
    padding: 0;
    position: relative;
    left: 0;
    width: 100%;
    max-width: 100vw;
    scroll-behavior: smooth;
  }

  /* ─── Loading ──────────────────────────────────────── */
  .pv-loading {
    min-height: 100vh;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .pv-spinner {
    width: 32px; height: 32px;
    border: 3px solid #e2e8f0;
    border-top-color: #0ea5e9;
    border-radius: 50%;
    animation: pv-spin 0.7s linear infinite;
  }
  @keyframes pv-spin { to { transform: rotate(360deg); } }

  /* ─── 404 ──────────────────────────────────────────── */
  .pv-notfound {
    min-height: 100vh;
    background: #fff;
    color: #1e293b;
    font-family: 'Inter', system-ui, sans-serif;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
  }
  .pv-notfound-code {
    font-size: 72px;
    font-weight: 900;
    letter-spacing: -0.04em;
    color: #0ea5e9;
    margin-bottom: 12px;
  }
  .pv-notfound-text { color: #64748b; font-size: 16px; margin-bottom: 32px; }
  .pv-link-home { color: #0ea5e9; text-decoration: none; font-size: 14px; font-weight: 600; }
  .pv-link-home:hover { text-decoration: underline; }

  /* ─── Nav ───────────────────────────────────────────── */
  .pv-nav {
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(255,255,255,0.95);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid #e2e8f0;
  }
  .pv-nav-inner {
    max-width: 860px;
    margin: 0 auto;
    padding: 14px 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .pv-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
    color: #0f172a;
  }
  .pv-logo-text { font-size: 15px; font-weight: 800; letter-spacing: -0.02em; }
  .pv-nav-right { display: flex; align-items: center; gap: 20px; }
  .pv-nav-link { color: #64748b; text-decoration: none; font-size: 13px; font-weight: 500; transition: color 0.2s; }
  .pv-nav-link:hover { color: #0f172a; }
  .pv-nav-cta {
    background: #0ea5e9;
    border: none;
    color: #fff;
    padding: 7px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.2s;
  }
  .pv-nav-cta:hover { background: #0284c7; }

  /* ─── Hero Header ──────────────────────────────────── */
  .pv-hero {
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 1px solid #e2e8f0;
    left: 0;
  }
  .pv-hero-glow {
    position: absolute;
    top: -80px; left: 50%;
    transform: translateX(-50%);
    width: 700px; height: 300px;
    background: radial-gradient(ellipse at center, rgba(14,165,233,0.08) 0%, transparent 70%);
    pointer-events: none;
  }
  .pv-hero::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230ea5e9' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    pointer-events: none;
  }
  .pv-hero-inner {
    max-width: 1200px;
    margin: 0 auto;
    padding: 56px 28px 48px;
    position: relative;
    text-align: left;
  }
  .pv-breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
    font-size: 13px;
  }
  .pv-breadcrumb a { color: #64748b; text-decoration: none; transition: color 0.2s; }
  .pv-breadcrumb a:hover { color: #0ea5e9; }
  .pv-breadcrumb span { color: #334155; }
  .pv-breadcrumb-sep { color: #cbd5e1; }
  .pv-title {
    font-size: clamp(28px, 4vw, 42px);
    font-weight: 800;
    letter-spacing: -0.025em;
    line-height: 1.15;
    color: #0f172a;
    margin: 0 0 12px;
    text-align: left;
  }
  .pv-date { color: #94a3b8; font-size: 13px; margin: 0; }
  .pv-edit-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    font-size: 12px;
    font-weight: 600;
    color: #0ea5e9;
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    border-radius: 8px;
    text-decoration: none;
    transition: all 0.2s;
    white-space: nowrap;
    shrink: 0;
  }
  .pv-edit-btn:hover {
    background: #e0f2fe;
    border-color: #7dd3fc;
    color: #0284c7;
  }

  /* ─── Content ──────────────────────────────────────── */
  .pv-main {
    max-width: 1200px;
    margin: 0 auto;
    padding: 48px 28px 80px;
    text-align: left;
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 40px;
  }

  .pv-toc {
    position: sticky;
    top: 120px;
    height: fit-content;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
  }
  .pv-toc-title {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 16px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .pv-toc-nav {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .pv-toc-link {
    color: #64748b;
    text-decoration: none;
    font-size: 13px;
    line-height: 1.4;
    padding: 4px 8px;
    border-radius: 6px;
    transition: all 0.2s;
    display: block;
  }
  .pv-toc-link:hover {
    color: #0ea5e9;
    background: #f0f9ff;
  }
  .pv-toc-level-1 {
    font-weight: 600;
    color: #1e293b;
    margin-top: 8px;
  }
  .pv-toc-level-2 {
    padding-left: 12px;
    font-weight: 500;
  }
  .pv-toc-level-3 {
    padding-left: 24px;
    font-size: 12px;
  }

  .pv-content {
    color: #475569;
    font-size: 15px;
    line-height: 1.8;
    text-align: left;
  }

  /* Headings */
  .pv-content h1 { display: none; }
  .pv-content h2 {
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    margin: 56px 0 20px;
    padding-bottom: 16px;
    border-bottom: 2px solid #e2e8f0;
    letter-spacing: -0.01em;
    text-align: left;
    position: relative;
  }
  .pv-content h2::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, #0ea5e9, #0284c7);
  }
  .pv-content h2:first-of-type { margin-top: 0; }
  .pv-content h3 {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin: 40px 0 16px;
    text-align: left;
    position: relative;
    padding-left: 20px;
  }
  .pv-content h3::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 4px;
    height: 16px;
    background: #0ea5e9;
    border-radius: 2px;
  }
  .pv-content h4 {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    margin: 28px 0 12px;
    text-align: left;
  }

  /* Paragraphs */
  .pv-content p {
    margin: 0 0 16px;
    color: #475569;
    text-align: left;
  }

  /* Links */
  .pv-content a {
    color: #0ea5e9;
    text-decoration: none;
    border-bottom: 1px solid rgba(14,165,233,0.3);
    transition: all 0.2s;
  }
  .pv-content a:hover {
    color: #0284c7;
    border-bottom-color: #0284c7;
  }

  /* Lists */
  .pv-content ul, .pv-content ol {
    margin: 0 0 24px;
    padding-left: 0;
    list-style: none;
    text-align: left;
  }
  .pv-content ul li, .pv-content ol li {
    position: relative;
    padding: 8px 0 8px 28px;
    color: #475569;
    font-size: 14px;
    line-height: 1.7;
    text-align: left;
    border-left: 2px solid transparent;
    transition: border-color 0.2s;
  }
  .pv-content ul li:hover, .pv-content ol li:hover {
    border-left-color: #0ea5e9;
  }
  .pv-content ul li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 16px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #0ea5e9;
    box-shadow: 0 0 0 2px rgba(14,165,233,0.1);
  }
  .pv-content ol {
    counter-reset: pv-ol;
  }
  .pv-content ol li {
    counter-increment: pv-ol;
  }
  .pv-content ol li::before {
    content: counter(pv-ol);
    position: absolute;
    left: 0;
    top: 8px;
    width: 18px;
    height: 18px;
    background: #0ea5e9;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    text-align: center;
  }

  /* Strong / bold */
  .pv-content strong {
    color: #1e293b;
    font-weight: 600;
  }

  /* Tables */
  .pv-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    font-size: 14px;
  }
  .pv-content th, .pv-content td {
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    text-align: left;
  }
  .pv-content th {
    background: #f8fafc;
    color: #1e293b;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .pv-content td { color: #475569; }

  /* Blockquotes */
  .pv-content blockquote {
    margin: 24px 0;
    padding: 20px 24px;
    border-left: 4px solid #0ea5e9;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border-radius: 0 12px 12px 0;
    color: #1e293b;
    font-style: italic;
    position: relative;
    box-shadow: 0 2px 8px rgba(14,165,233,0.1);
  }
  .pv-content blockquote::before {
    content: '"';
    position: absolute;
    top: -8px;
    left: 16px;
    font-size: 48px;
    color: #0ea5e9;
    opacity: 0.3;
    font-family: serif;
  }

  /* Code / pre */
  .pv-content code {
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 13px;
    color: #0f172a;
    font-family: 'SF Mono', 'Fira Code', monospace;
  }
  .pv-content pre {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px 20px;
    overflow-x: auto;
    margin: 20px 0;
  }
  .pv-content pre code {
    background: none;
    padding: 0;
  }

  /* Horizontal rule */
  .pv-content hr {
    border: none;
    border-top: 1px solid #e2e8f0;
    margin: 40px 0;
  }

  /* ─── Footer ───────────────────────────────────────── */
  .pv-footer {
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
  }
  .pv-footer-inner {
    max-width: 860px;
    margin: 0 auto;
    padding: 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }
  .pv-footer-company { color: #94a3b8; font-size: 12px; margin: 0; }
  .pv-footer-links { display: flex; gap: 20px; }
  .pv-footer-links a {
    color: #64748b;
    text-decoration: none;
    font-size: 12px;
    transition: color 0.2s;
  }
  .pv-footer-links a:hover { color: #0f172a; }

  /* ─── Mobile ───────────────────────────────────────── */
  @media (max-width: 768px) {
    .pv-hero-inner { padding: 36px 20px 32px; max-width: none; }
    .pv-title { font-size: 26px; }
    .pv-main {
      padding: 32px 20px 60px;
      grid-template-columns: 1fr;
      gap: 32px;
      max-width: none;
    }
    .pv-toc { position: static; }
    .pv-content h2 { font-size: 20px; margin-top: 36px; }
    .pv-content h3 { font-size: 17px; }
    .pv-nav-inner { padding: 12px 20px; }
    .pv-footer-inner {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }
    .pv-footer-links { flex-wrap: wrap; gap: 14px; }
  }
`;
